"use strict";
/**
 * Plausibility guards for sensor readings.
 *
 * A reading can be wrong in two different ways, and the homeowner surfaces have
 * to tell them apart: air that really is bad (a hazard — red), and a sensor
 * reporting a number no physical home can produce (a fault — amber, "the sensor
 * may need service"). Field examples that reached a dashboard: "PM2.5
 * 176144178222.00", "SEER 2.4", "EER 1.9 POOR".
 *
 * Guards belong at the aggregation/display boundary: an implausible value is
 * dropped from averages and from the status fold, and the row it came from is
 * flagged as needing attention with `INVALID_READING_*` copy — never with the
 * raw number, which is meaningless to a homeowner and looks like a bug.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatSeerLabel = exports.LOW_EFFICIENCY_MESSAGE = exports.LOW_EFFICIENCY_LABEL = exports.INVALID_READING_MESSAGE = exports.INVALID_READING_LABEL = exports.INVALID_READING_VALUE = exports.plausibleOrNull = exports.isPlausible = exports.PLAUSIBLE_RANGES = void 0;
/**
 * Inclusive bounds per metric.
 *
 * STARTING VALUES from the work order, pending Aidan's confirmation — they are
 * deliberately generous, sized to catch garbage rather than to grade a reading.
 * The `pm25` entry in particular needs a product decision: `AirQualityReading.pm`
 * is mg/m3 with a real sensor range of 0-0.8 (see `SensorReading.ts`) and
 * `pmToStatus` already thresholds at 0.28 / 0.68 on that scale, so 0-1000 —
 * a range written for ug/m3 — only rejects outright garbage like the
 * 176144178222 seen in the field and would still pass a reading wrong by 100x.
 */
exports.PLAUSIBLE_RANGES = {
    pm25: { min: 0, max: 1000 },
    seer: { min: 8, max: 30 },
    eer: { min: 5, max: 20 },
    iaq: { min: 0, max: 500 },
    temperatureF: { min: 30, max: 120 },
    humidityPct: { min: 0, max: 100 },
};
/**
 * True only for a genuine finite `number` inside the metric's inclusive range.
 *
 * Numeric-looking strings are rejected on purpose: GraphQL payloads hand some
 * readings back as strings, and silently coercing them here would hide the
 * parse that should have happened at the boundary.
 */
function isPlausible(metric, value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return false;
    const { min, max } = exports.PLAUSIBLE_RANGES[metric];
    return value >= min && value <= max;
}
exports.isPlausible = isPlausible;
/** The value when plausible, else null — for feeding averages and labels. */
function plausibleOrNull(metric, value) {
    return isPlausible(metric, value) ? value : null;
}
exports.plausibleOrNull = plausibleOrNull;
/** Placeholder that replaces the number itself. Em dash, not "N/A". */
exports.INVALID_READING_VALUE = '—';
/** Short form, for a status word where a band label would normally sit. */
exports.INVALID_READING_LABEL = 'Reading invalid';
/**
 * Long form: what happened, then what to do about it. Says "sensor" because the
 * sensor is what needs service — never "hub", and never a claim that a sensor
 * lives inside the base station.
 */
exports.INVALID_READING_MESSAGE = 'Reading invalid. The sensor may need service.';
/**
 * EER below the plausible floor is NOT "Reading invalid". Expertise, 2026-09-03:
 * running-hour EER 2.3 from a compressor drawing 20 A all hour against a 7°F
 * supply/return split at 648 CFM. That is either a coil that barely cools (low
 * charge, iced) or a misplaced probe — and a service visit checks both. So the
 * copy asks for one and never blames the sensor. Above the ceiling stays
 * "Reading invalid": no coil produces an EER of 45.
 */
exports.LOW_EFFICIENCY_LABEL = 'Very low efficiency';
/**
 * Long form for a surface with room for a sentence — the sensor-fault alert
 * body in the optional infrastructure follow-up. The home-card pills on BOTH
 * the hub and the phone show only LOW_EFFICIENCY_LABEL (Aidan, 09-03).
 */
exports.LOW_EFFICIENCY_MESSAGE = 'Very low efficiency. The system needs service.';
/**
 * Seasonal SEER headline, guarded.
 *
 * Returns null — NOT the invalid placeholder — for null/undefined, because
 * "no seasonal data yet" is a different condition from a bad reading and every
 * caller already has its own fallback for it (the avg EER label).
 */
function formatSeerLabel(value) {
    if (value === null || value === undefined)
        return null;
    if (!isPlausible('seer', value))
        return exports.INVALID_READING_VALUE;
    return `SEER ${value.toFixed(1)}`;
}
exports.formatSeerLabel = formatSeerLabel;
